/**
 * AdminSchemasTab Component
 *
 * Admin interface for managing multi-field Schemas.
 * Allows creating, editing, publishing, deprecating, and deleting schemas.
 * Features filtering by status and search functionality.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileCode, Search } from 'lucide-react';
import {
  schemaApi,
  Schema,
  SchemaStatus,
  SCHEMA_STATUSES,
} from '@/services/api/schemaApi';
import { SchemaCard } from './SchemaCard';
import { AddSchemaDialog } from './AddSchemaDialog';
import { EditSchemaDialog } from './EditSchemaDialog';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import * as errorUtils from '@/utils/errors';

export const AdminSchemasTab: React.FC = () => {
  // Data state
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SchemaStatus | 'all'>('all');
  const [orgFilter, setOrgFilter] = useState<string>('all');

  // Org list for the filter dropdown. Platform admins explicitly request the
  // platform schema scope, so allOrganizations is the relevant universe.
  // Non-admins only see their own orgs in `organizations`.
  const allOrganizations = useOrganizationStore((s) => s.allOrganizations);
  const memberOrganizations = useOrganizationStore((s) => s.organizations);
  const platformAdminEntitlement = useOrganizationStore((s) => s.platformAdminEntitlement);
  const orgsForFilter = platformAdminEntitlement ? allOrganizations : memberOrganizations;

  // Lookup table for displaying org names on cards. Built once per render of
  // the org list — for hundreds of orgs this is still trivial work.
  const orgNameById = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    for (const o of orgsForFilter) {
      m[o._id] = o.name;
    }
    return m;
  }, [orgsForFilter]);

  // Dialog states
  const [showAddSchema, setShowAddSchema] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [showEditSchema, setShowEditSchema] = useState(false);

  // Apply the org filter client-side. The backend already scoped to either
  // a single org or the explicitly requested platform scope; the client filter
  // narrows what platform admins see when they want to focus on one org.
  const visibleSchemas = useMemo(() => {
    if (orgFilter === 'all') return schemas;
    return schemas.filter((s) => s.organizationId === orgFilter);
  }, [schemas, orgFilter]);

  // Stats are computed over the visible set so they reflect what the user sees.
  const draftCount = visibleSchemas.filter((s) => s.status === 'draft').length;
  const publishedCount = visibleSchemas.filter((s) => s.status === 'published').length;
  const deprecatedCount = visibleSchemas.filter((s) => s.status === 'deprecated').length;
  const totalFields = visibleSchemas.reduce((sum, s) => sum + (s.fields?.length || 0), 0);

  // Fetch schemas. The data-service default page size is 20, capped at 100.
  // The admin panel does not yet have pagination controls, so we request the
  // maximum page to surface as many schemas as possible in one call. Platform
  // admins reviewing schemas across all orgs need to see the full set —
  // pagination UI is a follow-up.
  const fetchSchemas = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const options: {
        search?: string;
        status?: SchemaStatus;
        limit?: number;
        scope?: 'platform';
      } = platformAdminEntitlement
        ? { limit: 100, scope: 'platform' }
        : { limit: 100 };

      if (search.trim()) options.search = search.trim();
      if (statusFilter !== 'all') options.status = statusFilter;

      const result = await schemaApi.listSchemas(options);
      setSchemas(result.data);
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to load schemas');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [platformAdminEntitlement, search, statusFilter]);

  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSchemas();
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Handle publish schema
  const handlePublish = useCallback(
    async (schema: Schema) => {
      if (!window.confirm(`Are you sure you want to publish "${schema.displayName}"? This will make it available for use.`)) {
        return;
      }

      try {
        await schemaApi.publishSchema(schema.id);
        await fetchSchemas();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to publish schema');
        alert(errorMessage);
      }
    },
    [fetchSchemas]
  );

  // Handle deprecate schema
  const handleDeprecate = useCallback(
    async (schema: Schema) => {
      const reason = window.prompt(`Enter reason for deprecating "${schema.displayName}":`);
      if (reason === null) return; // Cancelled

      try {
        await schemaApi.deprecateSchema(schema.id, reason || 'No reason provided');
        await fetchSchemas();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to deprecate schema');
        alert(errorMessage);
      }
    },
    [fetchSchemas]
  );

  // Handle delete schema
  const handleDelete = useCallback(
    async (schema: Schema) => {
      if (!window.confirm(`Are you sure you want to delete "${schema.displayName}"? This action cannot be undone.`)) {
        return;
      }

      try {
        await schemaApi.deleteSchema(schema.id);
        await fetchSchemas();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to delete schema');
        alert(errorMessage);
      }
    },
    [fetchSchemas]
  );

  // Handle edit schema
  const handleEdit = useCallback((schema: Schema) => {
    setSelectedSchema(schema);
    setShowEditSchema(true);
  }, []);

  // Dialog success handlers
  const handleAddSchemaSuccess = useCallback(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  const handleEditSchemaSuccess = useCallback(() => {
    setShowEditSchema(false);
    setSelectedSchema(null);
    fetchSchemas();
  }, [fetchSchemas]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Schemas</h2>
            <p className="text-sm text-muted-foreground">
              Define data structures with multiple fields for your organization.
            </p>
          </div>
          <Button
            onClick={() => setShowAddSchema(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            + Create Schema
          </Button>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-6 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-yellow-500" />
            {draftCount} Draft
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            {publishedCount} Published
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gray-400" />
            {deprecatedCount} Deprecated
          </span>
          <span className="text-muted-foreground">
            {totalFields} total fields
          </span>
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search schemas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status filter */}
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as SchemaStatus | 'all')}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {SCHEMA_STATUSES.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Organization filter — only useful when results can span multiple
              orgs. Hidden when the user has fewer than 2 orgs to choose from. */}
          {orgsForFilter.length > 1 && (
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {[...orgsForFilter]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((org) => (
                    <SelectItem key={org._id} value={org._id}>
                      {org.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-muted-foreground">Loading schemas...</div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="p-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <div className="font-medium">Error loading schemas</div>
            <div className="text-sm mt-1">{error}</div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={fetchSchemas}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && visibleSchemas.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <FileCode className="h-12 w-12 text-muted-foreground mb-4" />
            <div className="text-lg font-medium">No schemas found</div>
            <div className="text-sm text-muted-foreground mb-4">
              {search || statusFilter !== 'all' || orgFilter !== 'all'
                ? 'Try adjusting your filters.'
                : 'Create your first schema to define data structures.'}
            </div>
            {!search && statusFilter === 'all' && orgFilter === 'all' && (
              <Button
                onClick={() => setShowAddSchema(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                + Create Your First Schema
              </Button>
            )}
          </div>
        )}

        {/* Schema list */}
        {!isLoading && !error && visibleSchemas.length > 0 && (
          <div className="space-y-3">
            {visibleSchemas.map((schema) => (
              <SchemaCard
                key={schema.id}
                schema={schema}
                orgName={orgNameById[schema.organizationId]}
                onEdit={() => handleEdit(schema)}
                onDelete={() => handleDelete(schema)}
                onPublish={() => handlePublish(schema)}
                onDeprecate={() => handleDeprecate(schema)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddSchemaDialog
        open={showAddSchema}
        onOpenChange={setShowAddSchema}
        onSuccess={handleAddSchemaSuccess}
      />

      {selectedSchema && (
        <EditSchemaDialog
          open={showEditSchema}
          onOpenChange={(open) => {
            setShowEditSchema(open);
            if (!open) setSelectedSchema(null);
          }}
          schema={selectedSchema}
          onSuccess={handleEditSchemaSuccess}
        />
      )}
    </div>
  );
};

export default AdminSchemasTab;
